import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addComment,
  deleteComment,
  getComments,
  updateComment,
} from '../../../shared/api/comments.api'
import type { Comment, ProcurementResult } from '../../../shared/api/types'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'
import { useAuthStore } from '../../auth/authStore'
import { SOURCE_LABELS } from './AddResultForm'
import { MentionInput, type MentionInputHandle } from '../../requests/components/MentionInput'
import { CommentItem } from '../../requests/components/CommentItem'
import { ReplyHintBar } from '../../requests/components/ReplyHintBar'
import {
  AttachImageButton,
  DraftImages,
  canSendComment,
  useCommentImages,
} from '../../requests/components/commentImages'

/**
 * Комментарий из ветки квоты: сам к квоте, помечен сервером как ветка квоты или
 * ответ (через любую глубину) на такой комментарий. Логист ветки квот не видит
 * (там цены), поэтому упомянуть его там нельзя — сервер молча уберёт упоминание.
 */
export function isQuoteThreadComment(comment: Comment, byId: Map<string, Comment>): boolean {
  const seen = new Set<string>()
  let cur: Comment | undefined = comment
  while (cur && !seen.has(cur.id)) {
    if (cur.quoteId || cur.quoteThread) return true
    seen.add(cur.id)
    cur = cur.parentId ? byId.get(cur.parentId) : undefined
  }
  return false
}

/** Короткая подпись квоты для бейджа: «<supplier> · <price> <currency> · <matrix>». */
function formatQuoteLabel(q: ProcurementResult): string {
  const parts = [q.supplierName]
  if (q.price != null) parts.push(`${q.price} ${q.currency}`)
  parts.push(SOURCE_LABELS[q.sourceType] ?? q.sourceType)
  return parts.join(' · ')
}

export function ItemCommentsPanel({
  itemId,
  requestId,
  quoteId,
  onClearQuote,
  focusCommentId,
  quotes = [],
}: {
  itemId: string
  /** ID заявки нужен для загрузки списка mentionable-users в @-popup. */
  requestId?: string
  quoteId?: string | null
  onClearQuote?: () => void
  focusCommentId?: string | null
  /** Квоты позиции — нужны, чтобы у комментариев с `quoteId` показать
   *  бейдж «к квоте: <supplier> · <price> · <matrix>». */
  quotes?: ProcurementResult[]
}) {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [commentText, setCommentText] = useState('')
  const [mentions, setMentions] = useState<string[]>([])
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const inputRef = useRef<MentionInputHandle>(null)
  const images = useCommentImages()

  const handleReply = useCallback((c: Comment) => {
    setReplyTo(c)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const quoteById = useMemo(() => {
    const m = new Map<string, ProcurementResult>()
    for (const q of quotes) m.set(q.id, q)
    return m
  }, [quotes])

  const selectedQuote = quoteId ? (quoteById.get(quoteId) ?? null) : null

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', 'item', itemId],
    queryFn: () => getComments({ requestItemId: itemId }),
    enabled: !!itemId,
  })

  // Логиста можно упомянуть только в переписке по позиции: не к квоте и не в
  // ответе внутри ветки квоты. Без itemId список упоминаний — по заявке, без него.
  const inQuoteThread = useMemo(() => {
    if (quoteId) return true
    if (!replyTo) return false
    const byId = new Map<string, Comment>()
    for (const c of comments) byId.set(c.id, c)
    return isQuoteThreadComment(replyTo, byId)
  }, [quoteId, replyTo, comments])

  const commentMut = useMutation({
    mutationFn: () =>
      addComment({
        text: commentText,
        requestItemId: itemId,
        quoteId,
        mentions,
        parentId: replyTo?.id ?? null,
        imageIds: images.ids,
      }),
    onSuccess: () => {
      setCommentText('')
      images.clear()
      setMentions([])
      setReplyTo(null)
      void queryClient.invalidateQueries({ queryKey: ['comments', 'item', itemId] })
    },
    // Текст не очищаем — можно поправить и отправить ещё раз.
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось отправить комментарий')),
  })

  const canSend = canSendComment(commentText, images) && !commentMut.isPending
  const send = () => {
    if (canSend) commentMut.mutate()
  }

  const updateMut = useMutation({
    mutationFn: ({ id, nextText }: { id: string; nextText: string }) =>
      updateComment(id, { text: nextText }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', 'item', itemId] })
    },
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось изменить комментарий')),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: (_data, id) => {
      if (replyTo?.id === id) setReplyTo(null)
      void queryClient.invalidateQueries({ queryKey: ['comments', 'item', itemId] })
    },
    onError: (err) => toast.error(apiErrorText(err, 'Не удалось удалить комментарий')),
  })

  const handleSubmitEdit = useCallback(
    async (id: string, nextText: string) => {
      await updateMut.mutateAsync({ id, nextText })
    },
    [updateMut],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteMut.mutateAsync(id)
    },
    [deleteMut],
  )

  const handleParentClick = useCallback((parentId: string) => {
    const el = document.querySelector<HTMLElement>(`[data-focus-id="${parentId}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('ring-2', 'ring-brand-300')
      setTimeout(() => el.classList.remove('ring-2', 'ring-brand-300'), 1500)
    }
  }, [])

  return (
    <div className="w-full lg:w-72 lg:shrink-0">
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-gray-700">Комментарии</h2>
          {quoteId && (
            <button
              type="button"
              onClick={onClearQuote}
              className="rounded bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:bg-brand-100 max-w-[180px] truncate"
              title={
                selectedQuote
                  ? `Комментарий будет привязан к квоте: ${formatQuoteLabel(selectedQuote)}`
                  : 'Комментарий будет привязан к выбранной квоте'
              }
            >
              К квоте{selectedQuote ? `: ${formatQuoteLabel(selectedQuote)}` : ''} ×
            </button>
          )}
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {comments.length === 0 ? (
            <p className="text-xs text-gray-400">Нет комментариев</p>
          ) : (
            comments.map((c) => {
              const linkedQuote = c.quoteId ? (quoteById.get(c.quoteId) ?? null) : null
              const badge = linkedQuote ? (
                <div
                  className="mb-1 inline-block rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700"
                  title={formatQuoteLabel(linkedQuote)}
                >
                  К квоте · {formatQuoteLabel(linkedQuote)}
                </div>
              ) : c.quoteId ? (
                <div className="mb-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                  К квоте · удалена
                </div>
              ) : null
              return (
                <CommentItem
                  key={c.id}
                  comment={c}
                  currentUserId={user?.id}
                  isFocused={focusCommentId === c.id}
                  onReply={user ? handleReply : undefined}
                  onSubmitEdit={user ? handleSubmitEdit : undefined}
                  onDelete={user ? handleDelete : undefined}
                  onParentClick={handleParentClick}
                  topBadge={badge}
                  containerClassName={linkedQuote ? 'border-l-2 border-brand-400' : ''}
                />
              )
            })
          )}
        </div>
        <div className="space-y-1">
          {replyTo && <ReplyHintBar parent={replyTo} onClear={() => setReplyTo(null)} />}
          <DraftImages state={images} />
          <div className="flex items-end gap-2">
            <MentionInput
              ref={inputRef}
              value={commentText}
              onChange={(t, m) => {
                setCommentText(t)
                setMentions(m)
              }}
              onSubmit={send}
              onPasteFiles={images.addFiles}
              allowEmptySubmit={canSend}
              requestId={requestId}
              itemId={inQuoteThread ? undefined : itemId}
              placeholder="Комментарий..."
              disabled={commentMut.isPending}
            />
            <AttachImageButton state={images} disabled={commentMut.isPending} />
            <button
              onClick={send}
              disabled={!canSend}
              className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
